import {useStoreContext} from '@store/store-context';
import {observer} from 'mobx-react';
import {FIREBASE_BASE_URL, database} from '@utils/firebase-request';
import {onValue, ref} from 'firebase/database';
import {GoalTasks} from './tasks/goal-tasks';
import {useMemo} from 'react';
import {AddMentions} from '@utils/firebase-request';
import {useState, useEffect, useCallback} from 'react';
import styled, {css} from 'styled-components';
import {CommentTab} from './comment/comment';
import {InsightTab} from './insight/insight';
import {TabNavigation} from '@ui/molecules/tab-nav';
import {IGoalResult} from '@hooks';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useActionItems} from '@hooks/features-action-items';
import {getColorFromTheme} from '@ui/ui-utils';

const TabCounter = styled.span`
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  border-radius: 50%;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font: 500 11px/16px Graphik, sans-serif;
`;

const ActivityTabs = styled.div<{$stickyTop?: number}>`
  ${({$stickyTop}) =>
    $stickyTop !== undefined &&
    css`
      position: sticky;
      z-index: 15;
      top: ${$stickyTop}px;
      margin: 0 -24px;
      padding: 20px 24px 0;
      background: ${getColorFromTheme('white')};
    `}
`;

export const MemberActivity = observer(
  ({
    goal,
    isReadOnly,
    stickyTop,
  }: {
    goal: IGoalResult;
    isReadOnly?: boolean;
    stickyTop?: number;
  }) => {
    const {
      storeDashboardRoutes: {InsightTabs, activateInsightTabs},
      goalsState: {handleClearComment},
      authStore: {auth},
    } = useStoreContext();

    const [goalSubscribers, setGoalSubscribers] = useState([]);
    const [mentions, setMentions] = useState<any>([]);

    const [currentUserMention, setCurrentUserMention] = useState<any>({
      unreadComment: 0,
      unreadTasks: 0,
      tasksMentionedBy: [],
      mentionedBy: [],
    });
    const {data: comments} = useFirebaseFetch(`goals/${goal.id}/comments`);
    const actionItems = useActionItems({
      source: 'goal',
      itemId: goal.id,
      userId: '',
      orderBy: {
        key: 'sourceId',
        value: goal.id,
      },
    });
    const commentCount = Array.isArray(comments)
      ? comments.filter(Boolean).length
      : Object.keys(comments || {}).length;
    const actionItemCount = actionItems.tasks.length;

    useEffect(() => {
      onValue(
        ref(
          database,
          `${FIREBASE_BASE_URL}/${auth.user.workspace.id}/users/${auth.user.id}/mentions/goals/${goal?.id}`,
        ),
        (snapshot) => {
          const response = snapshot.val();

          if (snapshot.exists()) {
            setCurrentUserMention(response);
          }
        },
      );
    }, [goal?.id, auth.user.id, auth.user.workspace.id]);

    useEffect(() => {
      onValue(
        ref(
          database,
          `${FIREBASE_BASE_URL}/${auth.user.workspace.id}/goals/${goal?.id}`,
        ),
        (snapshot: any) => {
          const subscribers = snapshot.val()?.subscribers;

          if (subscribers && subscribers.length) {
            setGoalSubscribers(subscribers);
            setMentions([]);
            subscribers.forEach((subscriber: string) => {
              onValue(
                ref(
                  database,
                  `${FIREBASE_BASE_URL}/${auth.user.workspace.id}/users/${subscriber}/mentions/goals/${goal?.id}`,
                ),
                (snapshot: any) => {
                  const response = snapshot.val();

                  if (response !== null && response) {
                    setMentions((prev: any) => [
                      ...prev,
                      {[subscriber]: response},
                    ]);
                  }
                },
              );
            });
          }
        },
      );
    }, [goal?.id, auth.user.id, auth.user.workspace.id]);

    const handleUserViewComments = useCallback(() => {
      AddMentions(
        auth.user.id,
        goal.id,
        {
          ...currentUserMention,
          unreadComment: 0,
          mentionedBy: [],
        },
        'goals',
      );
    }, [auth.user.id, goal.id, currentUserMention]);

    const navigations = useMemo(() => {
      return [
        {
          active: InsightTabs.insight,
          href: '/',
          id: '1',
          onClick: (e: any) => {
            e.preventDefault();
            activateInsightTabs('insight');
          },
          title: `Insights`,
        },
        {
          active: InsightTabs.comment,
          href: '/',
          id: '2',
          onClick: (e: any) => {
            e.preventDefault();
            activateInsightTabs('comment');
            handleUserViewComments();
            handleClearComment(goal?.id);
          },
          title: `Comments`,
          customtitle: (
            <TabCounter aria-label={`${commentCount} comments`}>
              {commentCount}
            </TabCounter>
          ),
        },
        {
          active: InsightTabs.tasks,
          href: '/',
          id: '3',
          onClick: (e: any) => {
            e.preventDefault();
            activateInsightTabs('tasks');
            handleClearComment(goal?.id);
          },
          title: `Action items`,
          customtitle: (
            <TabCounter aria-label={`${actionItemCount} action items`}>
              {actionItemCount}
            </TabCounter>
          ),
        },
      ];
    }, [
      InsightTabs.insight,
      InsightTabs.comment,
      handleClearComment,
      InsightTabs.tasks,
      commentCount,
      actionItemCount,
      goal?.id,
      activateInsightTabs,
      handleUserViewComments,
    ]);

    const getSubscribers = () => {
      const krs = goal.keyResults.map((item: any) => item.assignee);
      const assigneeGoals = [
        ...Array.from(
          new Map(krs.map((item: any) => [item['id'], item])).values(),
        ),
      ];

      const EachAssignee = assigneeGoals.map((kr: any) => kr.id);

      return EachAssignee.includes(goal.assignee?.id)
        ? EachAssignee
        : [goal.assignee?.id, ...EachAssignee];
    };

    return (
      <div>
        <ActivityTabs $stickyTop={stickyTop}>
          <TabNavigation navigations={navigations} />
        </ActivityTabs>
        {InsightTabs.comment && (
          <CommentTab
            id={goal?.id}
            handleMentionUser={handleUserViewComments}
            mentions={mentions}
            unreadComment={currentUserMention.unreadComment}
            isReadOnly={isReadOnly}
            defaultGoalSubscribers={getSubscribers()}
            allGoalSubscribers={goalSubscribers}
          />
        )}
        {InsightTabs.insight && <InsightTab goal={goal} />}
        {InsightTabs.tasks && (
          <GoalTasks
            goalId={goal.id}
            userId={goal.assignee?.id}
            isReadOnly={isReadOnly}
            unreadTasks={currentUserMention.unreadTasks}
            currentUserMentions={currentUserMention}
            mentions={mentions}
            defaultGoalSubscribers={getSubscribers()}
            allGoalSubscribers={goalSubscribers}
            actionItems={actionItems}
          />
        )}
      </div>
    );
  },
);
